/**
 * Query String Builder Utilities
 *
 * Common utilities for building Ransack-style query strings from filter state.
 * These helpers handle the common patterns found across filter contexts.
 */

/**
 * Adds a simple string filter to params if value is non-empty
 */
export const addStringFilter = (
  params: URLSearchParams,
  key: string,
  value: string | null | undefined,
): void => {
  const trimmed = value?.trim();
  if (trimmed) {
    params.append(key, trimmed);
  }
};

/**
 * Adds a search string as array filter elements (split by whitespace)
 * For Ransack _any predicates that expect multiple values
 * Example: "John Doe" → q[key][]=John&q[key][]=Doe
 */
export const addSearchStringAsArrayFilter = (
  params: URLSearchParams,
  key: string,
  value: string | null | undefined,
): void => {
  const trimmed = value?.trim();
  if (!trimmed) return;

  const terms = trimmed.split(/\s+/).filter(Boolean);
  terms.forEach((term) => {
    params.append(`${key}[]`, term);
  });
};

/**
 * Adds an array filter to params (comma-separated)
 */
export const addArrayFilter = (
  params: URLSearchParams,
  key: string,
  values: string[] | null | undefined,
): void => {
  if (values && values.length > 0) {
    params.append(key, values.join(","));
  }
};

/**
 * Adds array values individually (for APIs expecting multiple params)
 */
export const addArrayFilterIndividual = (
  params: URLSearchParams,
  key: string,
  values: string[] | null | undefined,
): void => {
  if (values && values.length > 0) {
    values.forEach((value) => {
      params.append(key, value);
    });
  }
};

/**
 * Adds a boolean filter to params
 */
export const addBooleanFilter = (
  params: URLSearchParams,
  key: string,
  value: boolean | null | undefined,
): void => {
  if (value === true) {
    params.append(key, "true");
  }
  else if (value === false) {
    params.append(key, "false");
  }
};

/**
 * Adds a date range filter (greater than or equal / less than or equal pattern)
 */
export const addDateRangeFilter = (
  params: URLSearchParams,
  baseKey: string,
  range: { from: string | null, to: string | null } | null | undefined,
): void => {
  if (range?.from) {
    params.append(`${baseKey}_gteq`, range.from);
  }
  if (range?.to) {
    params.append(`${baseKey}_lteq`, range.to);
  }
};

/**
 * Adds a Date object range filter (converts to ISO date string)
 */
export const addDateObjectRangeFilter = (
  params: URLSearchParams,
  baseKey: string,
  range: { from: Date | undefined, to: Date | undefined } | null | undefined,
): void => {
  if (range?.from) {
    params.append(`${baseKey}_gteq`, range.from.toISOString().split("T")[0]);
  }
  if (range?.to) {
    params.append(`${baseKey}_lteq`, range.to.toISOString().split("T")[0]);
  }
};

/**
 * Adds custom attribute filters (dynamic keys)
 */
export const addCustomAttributeFilters = (
  params: URLSearchParams,
  customAttributes: Record<string, unknown> | null | undefined,
  keyPrefix = "q[custom_attributes_",
): void => {
  if (!customAttributes) return;

  Object.entries(customAttributes).forEach(([attributeId, value]) => {
    if (value === null || value === undefined || value === "") return;

    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.append(`${keyPrefix}${attributeId}]`, value.join(","));
      }
    }
    else if (typeof value === "boolean") {
      params.append(`${keyPrefix}${attributeId}]`, value.toString());
    }
    else {
      params.append(`${keyPrefix}${attributeId}]`, String(value));
    }
  });
};

/**
 * Adds an age range filter with reference date
 */
export const addAgeRangeFilter = (
  params: URLSearchParams,
  ageMin: number | null | undefined,
  ageMax: number | null | undefined,
  referenceDate: string,
): void => {
  if (ageMin !== null && ageMin !== undefined) {
    params.append("q[age_min][]", ageMin.toString());
    params.append("q[age_min][]", referenceDate);
  }
  if (ageMax !== null && ageMax !== undefined) {
    params.append("q[age_max][]", ageMax.toString());
    params.append("q[age_max][]", referenceDate);
  }
};

/**
 * Adds a filter only if value is not "all"
 */
export const addEnumFilter = (
  params: URLSearchParams,
  key: string,
  value: string | null | undefined,
  allValue = "all",
): void => {
  if (value && value !== allValue) {
    params.append(key, value);
  }
};
